<template>
  <PageView class="bg-light" header-class="bg-light">
    <div class="d-flex justify-content-center">
      <div class="col-md-8 card">
        <div class="card-body">
            <h3 class="card-title text-center mb-4">Mon profil</h3>
            <div class="card-text">
              <div class="row">
                <div class="col-12 col-sm-6">
                  <SelectInputComponent
                    value-key="civility"
                    label="Civilité"
                    placeholder="Sélectionnez une civilité"
                    :required="true"
                    class="mb-3"
                    :options="Object.values(CivilityEnum).map(type => ({ value: type, label: GetCivilityShortLabel(type) }))"
                    @change="save"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-12 col-sm-6">
                  <TextInputComponent
                    value-key="lastName"
                    label="Nom"
                    placeholder="Nom"
                    :required="true"
                    class="mb-3"
                    :upper-case="true"
                    @change="save"
                  />
                </div>
                <div class="col-12 col-sm-6">
                  <TextInputComponent
                    value-key="firstName"
                    label="Prénom"
                    placeholder="Prénom"
                    :required="true"
                    class="mb-3"
                    :capitalize="true"
                    @change="save"
                  />
                </div>
                <div class="col-12 col-md-6">
                  <TextInputComponent
                    value-key="email"
                    label="Email"
                    placeholder="Email"
                    :required="true"
                    class="mb-3"
                    type="email"
                    @change="save"
                  />
                </div>
                <div class="col-12 col-md-6">
                  <PhoneInputComponent
                    value-key="mobilePhone"
                    label="Téléphone"
                    placeholder="Téléphone"
                    class="mb-3"
                    v-model:is-valid="isMobilePhoneValid"
                    :required="true"
                    @change="save"
                  />
                </div>
            </div>

            <hr />
            <div class="row mt-md-4">
                <div class="col-12 col-md-6">
                  <SelectInputComponent
                    value-key="nationality"
                    label="Nationalité"
                    placeholder="Sélectionnez une nationalité"
                    :required="false"
                    class="mb-3"
                    :options="Nationalities"
                    @change="save"
                  />
                </div>
                <div class="col-12 col-md-6">
                  <DatePickerComponent
                    value-key="birthDate"
                    label="Date de naissance"
                    placeholder="Date de naissance"
                    :required="false"
                    class="mb-3"
                    :max-date="new Date()"
                    @change="save"
                  />
                </div>
              </div>

              <hr  />
              <div class="row mt-md-4">
                <div class="col-12 col-md-6">
                  <TextInputComponent
                    value-key="passportNumber"
                    label="Numéro de passeport"
                    placeholder="Numéro de passeport"
                    :required="false"
                    class="mb-3"
                    @change="save"
                  />
                </div>
                <div class="col-12 col-md-6">
                  <DatePickerComponent
                    value-key="passportExpirationDate"
                    label="Date d'expiration du passeport"
                    placeholder="Date d'expiration du passeport"
                    :required="false"
                    class="mb-3"
                    :min-date="new Date()"
                    @change="save"
                  />
                </div>
              </div>

            </div>
        </div>
      </div>
    </div>
  </PageView>
</template>

<script setup lang="ts">
import * as zod from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { useNotificationsStore } from '@/stores/notificationStore';
import PageView from '@/views/PageView.vue';
import { GetCivilityShortLabel } from '@/compositions/helper';
import { Nationalities } from '@/compositions/nationalities';

import { useUserStore } from '@/stores/userStore';
import userApi from '@/api/userApi';

const notificationsStore = useNotificationsStore();
const userStore = useUserStore()

const isMobilePhoneValid = defineModel<boolean>('isMobilePhoneValid');

const schema = toTypedSchema(
  zod.object({
    civility: zod
      .nativeEnum(CivilityEnum, {message: 'La civilité est obligatoire' }),
    lastName: zod
      .string()
      .min(1, { message: "Le nom est requis" }),
    firstName: zod
      .string()
      .min(1, { message: "Le prénom est requis" }),
    email: zod
      .string()
      .email({ message: "L'email doit être valide" })
      .optional()
      .or(zod.literal('')),
    mobilePhone: zod
      .string()
      .min(1, { message: "Le téléphone est requis" })
      .optional(),
    birthDate: zod
      .date({ message: "La date de naissance est requise" })
      .optional(),
    nationality: zod
      .string()
      .optional(),
    passportNumber: zod
      .string()
      .min(5, "Numéro de passeport trop court")
      .max(50, "Numéro de passeport trop long")
      .optional()
      .nullish()
      .or(zod.literal('')),
    passportExpirationDate: zod
      .date({message: 'La date d\'expiration du passeport est obligatoire' }).nullish().or(zod.literal('')),
  })
);

const {
  handleSubmit,
  resetForm,
  values: formValues,
} = useForm<UserMe>({
  initialValues: userStore.currentUser,
  validationSchema: schema,
  keepValuesOnUnmount: true,
  validateOnMount: false,
});

const save = async () => {
  return await handleSubmit(async () => {
    return await userApi.updateMe(formValues)
    .then((response) => {
        resetForm({values: response.data}, { force: true });
        userStore.setCurrentUser(response.data);
        return true;
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de l\'enregistrement',error.message);
      return false;
    });
  },(result) => {
      const errors = [...new Set(Object.keys(result.errors)
      .map((key:string) => {
        return result.errors[key as keyof typeof result.errors]
      }))];
      notificationsStore.notifyError(errors.slice(0, 5).join('<br>'));
      return false;
  })()
};
</script>
